/*
 * platform.ts — runtime OS detection + shortcut formatter.
 *
 * Shortcuts are STORED in their verbose, Windows/Linux-friendly form
 * ("Ctrl+Shift+S", "Ctrl+,") in the commands list and component props.
 * At render time `formatShortcut()` rewrites them to Mac symbols
 * (⌘⇧S, ⌘,) when the user is on a Mac. This way:
 *
 *   - one canonical string per shortcut, no per-component branching
 *   - users see the symbols their platform actually uses
 *   - test/snapshot output is platform-stable (formatShortcut is the
 *     only place that branches on IS_MAC; tests can mock it if needed)
 *
 * Detection prefers `navigator.userAgentData.platform` when available
 * (Chromium 90+, the standard going forward), falls back to legacy
 * `navigator.platform`, then degrades to false on the server.
 */

interface UserAgentDataLike {
  platform?: string
}

function detectMac(): boolean {
  if (typeof navigator === 'undefined') return false
  const uaData = (navigator as Navigator & { userAgentData?: UserAgentDataLike }).userAgentData
  if (uaData?.platform) {
    return /mac/i.test(uaData.platform)
  }
  const legacy = navigator.platform || ''
  if (legacy) return /Mac|iPhone|iPad|iPod/i.test(legacy)
  return /Mac|iPhone|iPad|iPod/i.test(navigator.userAgent || '')
}

export const IS_MAC = detectMac()

const MAC_MAP: Record<string, string> = {
  CTRL: '⌘',
  CMD: '⌘',
  META: '⌘',
  SHIFT: '⇧',
  ALT: '⌥',
  OPT: '⌥',
  OPTION: '⌥',
  ENTER: '↵',
  RETURN: '↵',
  ESC: 'Esc',
  ESCAPE: 'Esc',
}

/**
 * Convert a verbose shortcut spec to platform-appropriate display form.
 *
 * Mac path joins modifiers with no separator (⌘⇧S is the convention,
 * not "⌘+⇧+S"). Windows/Linux path keeps the "+" separators and the
 * verbose modifier names that users actually see on their keyboards.
 *
 * Unknown tokens pass through unchanged so e.g. "Ctrl+Alt+F12" still
 * renders the F12 part as-is.
 */
export function formatShortcut(spec: string): string {
  if (!spec) return ''
  const parts = spec.split('+').map((p) => p.trim())
  if (IS_MAC) {
    return parts
      .map((p) => MAC_MAP[p.toUpperCase()] ?? p)
      .join('')
  }
  return parts.join('+')
}
